import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { Store, Loader2 } from "lucide-react";

const CATEGORIES = [
  "Vino", "Olio EVO", "Salumi", "Formaggi", "Pasta", "Conserve",
  "Dolci", "Caffè", "Caviale & Roe", "Tonno & Pesce", "Altro",
];

export default function VendorRegister() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    company: "", vat_number: "", name: "", email: "", password: "",
    phone: "", country: "IT", city: "", category: "Vino",
    store_name: "", store_description: "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.vat_number || !form.name || !form.email || form.password.length < 6) {
      toast.error("Compila tutti i campi obbligatori (password min 6 caratteri).");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, store_name: form.store_name || form.company };
      const r = await api.post("/vendor/register", payload);
      localStorage.setItem("ti_token", r.data.token);
      localStorage.setItem("ti_user", JSON.stringify(r.data.user));
      setUser(r.data.user);
      toast.success("Azienda registrata! Benvenuto nel tuo pannello.");
      navigate("/azienda");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore durante la registrazione.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full px-4 py-2.5 border border-border rounded-sm bg-card focus:outline-none focus:border-[hsl(var(--primary))] text-sm";

  return (
    <div data-testid="vendor-register-page" className="container-xl py-12 max-w-3xl">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[hsl(var(--primary))]/10 mb-3">
          <Store className="h-7 w-7 text-[hsl(var(--primary))]" />
        </div>
        <h1 className="editorial-heading text-4xl">Registra la tua Azienda</h1>
        <p className="text-muted-foreground mt-2">
          Crea la tua vetrina, carica prodotti e vendi a pezzi (B2C) o a blocchi (B2B).
        </p>
      </div>

      <form onSubmit={submit} className="bg-card border border-border rounded-sm p-6 md:p-8 space-y-6">
        {/* Dati azienda */}
        <div>
          <h2 className="text-sm uppercase tracking-[0.2em] text-[hsl(var(--primary))] font-semibold mb-4">
            Dati azienda
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Ragione sociale *</label>
              <input data-testid="vr-company" className={inputCls} value={form.company} onChange={set("company")} />
            </div>
            <div>
              <label className="text-sm font-medium">Partita IVA *</label>
              <input data-testid="vr-vat" className={inputCls} value={form.vat_number} onChange={set("vat_number")} />
            </div>
            <div>
              <label className="text-sm font-medium">Categoria merceologica</label>
              <select data-testid="vr-category" className={inputCls} value={form.category} onChange={set("category")}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Città</label>
              <input className={inputCls} value={form.city} onChange={set("city")} />
            </div>
            <div>
              <label className="text-sm font-medium">Paese</label>
              <input className={inputCls} value={form.country} onChange={set("country")} />
            </div>
            <div>
              <label className="text-sm font-medium">Telefono</label>
              <input className={inputCls} value={form.phone} onChange={set("phone")} />
            </div>
          </div>
        </div>

        {/* Referente / accesso */}
        <div>
          <h2 className="text-sm uppercase tracking-[0.2em] text-[hsl(var(--primary))] font-semibold mb-4">
            Referente e accesso
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Nome referente *</label>
              <input data-testid="vr-name" className={inputCls} value={form.name} onChange={set("name")} />
            </div>
            <div>
              <label className="text-sm font-medium">Email *</label>
              <input data-testid="vr-email" type="email" className={inputCls} value={form.email} onChange={set("email")} />
            </div>
            <div>
              <label className="text-sm font-medium">Password * (min 6)</label>
              <input data-testid="vr-password" type="password" className={inputCls} value={form.password} onChange={set("password")} />
            </div>
          </div>
        </div>

        {/* Vetrina */}
        <div>
          <h2 className="text-sm uppercase tracking-[0.2em] text-[hsl(var(--primary))] font-semibold mb-4">
            La tua vetrina
          </h2>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="text-sm font-medium">Nome vetrina (opzionale, default = ragione sociale)</label>
              <input className={inputCls} value={form.store_name} onChange={set("store_name")} placeholder={form.company} />
            </div>
            <div>
              <label className="text-sm font-medium">Descrizione</label>
              <textarea className={inputCls} rows={3} value={form.store_description} onChange={set("store_description")} />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3 pt-2">
          <p className="text-xs text-muted-foreground">
            Hai già un account?{" "}
            <Link to="/login" className="text-[hsl(var(--primary))] hover:underline">Accedi</Link>
          </p>
          <button
            data-testid="vr-submit"
            type="submit"
            disabled={loading}
            className="inline-flex items-center gap-2 px-8 py-3 bg-[hsl(var(--primary))] text-white rounded-sm text-sm uppercase tracking-[0.15em] font-semibold hover:opacity-90 disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Crea vetrina
          </button>
        </div>
      </form>
    </div>
  );
}
